const cartInventoryStatusManager = {
  inventoryCache: {},
  getSelectedStore() {
    const selectedStoreRaw = localStorage.getItem("selectedStore");
    const fallbackStoreRaw = localStorage.getItem("selectedStoreCart");
    const raw = selectedStoreRaw || fallbackStoreRaw;

    if (!raw) return null;

    try {
      const store = JSON.parse(raw);

      if (!selectedStoreRaw && fallbackStoreRaw && store?.name) {
        localStorage.setItem("selectedStore", JSON.stringify(store));
      }

      return store;
    } catch (e) {
      return null;
    }
  },
  getInventoryCacheKey({ endpoint, skus, quantities = {}, siteCode }) {
    return [
      endpoint,
      siteCode || "",
      [...skus].sort().join(","),
      JSON.stringify(
        Object.keys(quantities)
          .sort()
          .reduce((accumulator, sku) => {
            accumulator[sku] = quantities[sku];
            return accumulator;
          }, {}),
      ),
    ].join("::");
  },
  normalizeInventoryEntries(data) {
    const candidates =
      data?.items ||
      data?.data?.items ||
      data?.results ||
      data?.data?.results ||
      data?.inventories ||
      data?.data?.inventories ||
      data;

    if (Array.isArray(candidates)) return candidates;

    if (
      candidates &&
      typeof candidates === "object" &&
      !Array.isArray(candidates) &&
      !("sku" in candidates) &&
      !("is_available" in candidates) &&
      !("available" in candidates)
    ) {
      return Object.entries(candidates).map(([sku, value]) => ({
        sku,
        ...(typeof value === "object" && value !== null
          ? value
          : { inventory: value }),
      }));
    }

    return candidates ? [candidates] : [];
  },
  getInventoryQuantity(data) {
    const quantity = Number(
      data?.inventory ?? data?.available ?? data?.data?.inventory,
    );
    return Number.isFinite(quantity) && quantity >= 0 ? quantity : null;
  },
  getInventoryStatus(data) {
    const inventory = this.getInventoryQuantity(data);
    const hasExplicitAvailability =
      data?.is_available !== undefined || data?.available !== undefined;
    const inferredAvailability = inventory !== null ? inventory > 0 : false;

    return {
      available: hasExplicitAvailability
        ? Boolean(data?.is_available ?? data?.available)
        : inferredAvailability,
      inventory,
    };
  },
  async fetchLocalInventoryStatuses({ skus, quantities, siteCode }) {
    const cacheKey = this.getInventoryCacheKey({
      endpoint: "local",
      skus,
      quantities,
      siteCode,
    });

    if (!this.inventoryCache[cacheKey]) {
      this.inventoryCache[cacheKey] = fetch(
        "https://shopify-big.bigcaring.com.my/api/outlet/check-inventory/local",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            skus,
            quantities,
            site_code: siteCode,
          }),
        },
      )
        .then((res) => res.json())
        .then((data) => {
          const inventoryMap = new Map();

          this.normalizeInventoryEntries(data).forEach((entry) => {
            const sku = String(entry?.sku || entry?.code || "").trim();
            if (!sku) return;
            inventoryMap.set(sku, this.getInventoryStatus(entry));
          });

          return inventoryMap;
        })
        .catch((error) => {
          delete this.inventoryCache[cacheKey];
          throw error;
        });
    }

    return this.inventoryCache[cacheKey];
  },
  getDeliverySkus(itemEntries) {
    return [...new Set(
      itemEntries
        .filter(({ item }) => item.dataset.pickupOnly !== "true")
        .map(({ sku }) => sku),
    )];
  },
  async fetchShopifyInventoryStatuses({ skus }) {
    if (skus.length === 0) return new Map();

    const cacheKey = this.getInventoryCacheKey({
      endpoint: "shopify",
      skus,
    });

    if (!this.inventoryCache[cacheKey]) {
      this.inventoryCache[cacheKey] = fetch(
        "https://shopify-big.bigcaring.com.my/api/outlet/check-inventory/shopify",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ skus }),
        },
      )
        .then((res) => res.json())
        .then((data) => {
          const inventoryMap = new Map();

          this.normalizeInventoryEntries(data).forEach((entry) => {
            const sku = String(entry?.sku || entry?.code || "").trim();
            if (!sku) return;
            inventoryMap.set(sku, this.getInventoryStatus(entry));
          });

          return inventoryMap;
        })
        .catch((error) => {
          delete this.inventoryCache[cacheKey];
          throw error;
        });
    }

    return this.inventoryCache[cacheKey];
  },
  getInventoryElement(item) {
    let el = item.querySelector(".cart-item__inventory");

    if (!el) {
      el = document.createElement("div");
      el.className = "cart-item__inventory";
      item.querySelector(".cart-item__details")?.appendChild(el);
    }

    let deliveryEl = el.querySelector("[data-cart-delivery-status]");
    if (!deliveryEl) {
      deliveryEl = document.createElement("div");
      deliveryEl.setAttribute("data-cart-delivery-status", "");
      el.appendChild(deliveryEl);
    }

    let pickupEl = el.querySelector("[data-cart-pickup-status]");
    if (!pickupEl) {
      pickupEl = document.createElement("div");
      pickupEl.setAttribute("data-cart-pickup-status", "");
      el.appendChild(pickupEl);
    }

    return { el, deliveryEl, pickupEl };
  },
  renderDeliveryStatus(item, deliveryStatus) {
    const { deliveryEl } = this.getInventoryElement(item);
    deliveryEl.hidden = item.dataset.pickupOnly === "true";
    if (deliveryEl.hidden) {
      deliveryEl.innerHTML = "";
      return;
    }

    const deliveryInventory = deliveryStatus?.inventory;

    if (deliveryInventory > 10) {
      deliveryEl.innerHTML = `
        <span style="color:#000;font-size:12px;display:flex;align-items:flex-start;gap:4px;margin-bottom:4px;">
          <span aria-hidden="true" style="display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:20px;height:20px;">
            <span style="display:block;width:12px;height:12px;border-radius:50%;background:#1f6bff;box-shadow:0 0 0 0.4rem rgba(31,107,255,0.12);"></span>
          </span>
          <span>Home Delivery Available</span>
        </span>
      `;
      return;
    }

    if (deliveryInventory > 0) {
      deliveryEl.innerHTML = `
        <span style="color:#000;font-size:12px;display:flex;align-items:flex-start;gap:4px;margin-bottom:4px;">
          <span aria-hidden="true" style="display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:20px;height:20px;">
            <span style="display:block;width:12px;height:12px;border-radius:50%;background:#1f6bff;box-shadow:0 0 0 0.4rem rgba(31,107,255,0.12);"></span>
          </span>
          <span>Home Delivery: Only ${deliveryInventory} left</span>
        </span>
      `;
      return;
    }

    deliveryEl.innerHTML = `
      <span style="color:#000;font-size:12px;display:flex;align-items:flex-start;gap:4px;margin-bottom:4px;">
        <span aria-hidden="true" style="display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:20px;height:20px;">
          <span style="display:block;width:12px;height:12px;border-radius:50%;background:#1f6bff;box-shadow:0 0 0 0.4rem rgba(31,107,255,0.12);"></span>
        </span>
        <span>Unavailable for Home Delivery.</span>
      </span>
    `;
  },
  renderPickupStatus(item, pickupStatus, requestedQuantity, store) {
    const { pickupEl } = this.getInventoryElement(item);

    if (!store || !store.name) {
      pickupEl.innerHTML = "";
      return;
    }

    const hasLimitedPickupInventory =
      pickupStatus?.inventory !== null &&
      pickupStatus?.inventory > 0 &&
      pickupStatus?.inventory < requestedQuantity;

    pickupEl.innerHTML = `
      <span style="color:#000;font-size:12px;display:flex;align-items:flex-start;gap:4px;">
        <span aria-hidden="true" style="display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:20px;height:20px;">
          <span style="display:block;width:12px;height:12px;border-radius:50%;background:#0c7a2d;box-shadow:0 0 0 0.4rem rgba(12,122,45,0.12);"></span>
        </span>
        <span>Pickup available at ${store.name}</span>
      </span>
    `;

    if (hasLimitedPickupInventory) {
      pickupEl.innerHTML = `
        <span style="color:#c76900;font-size:12px;display:flex;align-items:flex-start;gap:4px;">
          <span aria-hidden="true" style="display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:20px;height:20px;">
            <span style="display:block;width:12px;height:12px;border-radius:50%;background:#0c7a2d;box-shadow:0 0 0 0.4rem rgba(12,122,45,0.12);"></span>
          </span>
          <span>Only ${pickupStatus.inventory} available at ${store.name}</span>
        </span>
      `;
      return;
    }

    if (!pickupStatus?.available) {
      pickupEl.innerHTML = `
        <span style="color:red;font-size:12px;display:flex;align-items:flex-start;gap:4px;">
          <span aria-hidden="true" style="display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:20px;height:20px;">
            <span style="display:block;width:12px;height:12px;border-radius:50%;background:#0c7a2d;box-shadow:0 0 0 0.4rem rgba(12,122,45,0.12);"></span>
          </span>
          <span>Pickup unavailable at ${store.name}</span>
        </span>
      `;
      return;
    }
  },
  refresh(root) {
    if (!root) return Promise.resolve(new Map());

    const items = Array.from(root.querySelectorAll(".cart-item"));
    const store = this.getSelectedStore();
    const itemEntries = items
      .map((item) => {
        const sku = String(item.dataset.sku || "").trim();
        if (!sku) return null;

        return {
          item,
          sku,
          requestedQuantity:
            Number(item.querySelector(".quantity__input")?.value) || 1,
        };
      })
      .filter(Boolean);

    if (itemEntries.length === 0) return Promise.resolve(new Map());

    const quantities = itemEntries.reduce((accumulator, entry) => {
      accumulator[entry.sku] =
        (accumulator[entry.sku] || 0) + entry.requestedQuantity;
      return accumulator;
    }, {});

    const deliveryPromise = this.fetchShopifyInventoryStatuses({
      skus: this.getDeliverySkus(itemEntries),
    }).then((deliveryStatuses) => {
      itemEntries.forEach(({ item, sku }) => {
        const deliveryStatus = deliveryStatuses.get(sku) || {
          available: false,
          inventory: null,
        };
        this.renderDeliveryStatus(item, deliveryStatus);
      });
      return deliveryStatuses;
    });

    const pickupPromise = store?.siteup
      ? this.fetchLocalInventoryStatuses({
          skus: Object.keys(quantities),
          quantities,
          siteCode: store.siteup,
        }).then((pickupStatuses) => {
          itemEntries.forEach(({ item, sku }) => {
            const pickupStatus = pickupStatuses.get(sku) || {
              available: false,
              inventory: null,
            };
            this.renderPickupStatus(item, pickupStatus, quantities[sku], store);
          });
          return pickupStatuses;
        })
      : Promise.resolve().then(() => {
          itemEntries.forEach(({ item }) => {
            this.renderPickupStatus(item, null, 0, null);
          });
          return new Map();
        });

    return Promise.all([deliveryPromise, pickupPromise]).then(
      ([, pickupStatuses]) => pickupStatuses,
    );
  },
};

window.cartInventoryStatusManager = cartInventoryStatusManager;

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.inventoryCache = {};
    this.cachedLatLng = null;
    this.deliveryRequestId = 0;
    this.pickupRequestId = 0;
    this.onSelectedStoreChanged = this.onSelectedStoreChanged.bind(this);
    window.addEventListener(
      "selected-store:changed",
      this.onSelectedStoreChanged,
    );
    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this),
    );
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    window.removeEventListener(
      "selected-store:changed",
      this.onSelectedStoreChanged,
    );
  }

  onSelectedStoreChanged(event) {
    this.cachedLatLng = null;
    this.inventoryCache = {};
    cartInventoryStatusManager.inventoryCache = {};
    const shouldShowWarning = event?.detail?.source === "cart";
    this.loadCartInventory({ showWarning: shouldShowWarning });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    // ✅ 每次重新请求，但不要在仅仅打开购物车时弹缺货提示
    this.loadCartInventory({ showWarning: false });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this),
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getSelectedStore() {
    const selectedStoreRaw = localStorage.getItem("selectedStore");
    const fallbackStoreRaw = localStorage.getItem("selectedStoreCart");
    const raw = selectedStoreRaw || fallbackStoreRaw;

    if (!raw) return null;

    try {
      const store = JSON.parse(raw);

      // Backfill legacy cart-only key into the unified key.
      if (!selectedStoreRaw && fallbackStoreRaw && store?.name) {
        localStorage.setItem("selectedStore", JSON.stringify(store));
      }

      return store;
    } catch (e) {
      return null;
    }
  }

  async getLatLngFromSelectedStore() {
    if (this.cachedLatLng) return this.cachedLatLng;

    const store = this.getSelectedStore();
    if (!store) return null;

    const address = store.name;

    if (!address) return null;

    return new Promise((resolve) => {
      if (!window.google || !google.maps) {
        resolve(null);
        return;
      }

      const geocoder = new google.maps.Geocoder();

      geocoder.geocode({ address }, (results, status) => {
        if (status !== "OK") {
          resolve(null);
          return;
        }

        const loc = results[0].geometry.location;

        this.cachedLatLng = {
          lat: loc.lat(),
          lng: loc.lng(),
        };
        console.log(this.cachedLatLng);
        resolve(this.cachedLatLng);
      });
    });
  }
  hideInventoryWarning() {
    const modal = this.querySelector("#InventoryWarning");
    modal?.classList.add("hidden");
  }

  async loadCartInventory(options = {}) {
    const { showWarning = false } = options;
    const items = this.querySelectorAll(".cart-item");
    const store = this.getSelectedStore();

    const itemEntries = Array.from(items)
      .map((item) => {
        const sku = String(item.dataset.sku || "").trim();
        if (!sku) return null;

        return {
          item,
          sku,
          requestedQuantity:
            Number(item.querySelector(".quantity__input")?.value) || 1,
        };
      })
      .filter(Boolean);

    if (itemEntries.length === 0) {
      this.unavailableItems = [];
      this.hideInventoryWarning();
      return;
    }

    const deliveryRequestId = ++this.deliveryRequestId;
    const pickupRequestId = ++this.pickupRequestId;

    const quantities = itemEntries.reduce((accumulator, entry) => {
      accumulator[entry.sku] =
        (accumulator[entry.sku] || 0) + entry.requestedQuantity;
      return accumulator;
    }, {});

    const unavailableItems = [];
    this.hideInventoryWarning();

    try {
      const deliveryStatuses =
        await cartInventoryStatusManager.fetchShopifyInventoryStatuses({
          skus: cartInventoryStatusManager.getDeliverySkus(itemEntries),
        });

      if (deliveryRequestId === this.deliveryRequestId) {
        itemEntries.forEach(({ item, sku }) => {
          const deliveryStatus = deliveryStatuses.get(sku) || {
            available: false,
            inventory: null,
          };
          cartInventoryStatusManager.renderDeliveryStatus(item, deliveryStatus);
        });
      }
    } catch (e) {
      console.error(e);
    }

    if (!store?.siteup) {
      if (pickupRequestId !== this.pickupRequestId) return;

      itemEntries.forEach(({ item }) => {
        cartInventoryStatusManager.renderPickupStatus(item, null, 0, null);
      });

      this.unavailableItems = [];
      this.hideInventoryWarning();
      return;
    }

    try {
      const inventoryStatuses =
        await cartInventoryStatusManager.fetchLocalInventoryStatuses({
          skus: Object.keys(quantities),
          quantities,
          siteCode: store.siteup,
        });

      if (pickupRequestId !== this.pickupRequestId) return;

      itemEntries.forEach(({ item, sku, requestedQuantity }) => {
        const inventoryStatus = inventoryStatuses.get(sku) || {
          available: false,
          inventory: null,
        };
        cartInventoryStatusManager.renderPickupStatus(
          item,
          inventoryStatus,
          quantities[sku],
          store,
        );

        const hasLimitedInventory =
          store?.siteup &&
          inventoryStatus.inventory !== null &&
          inventoryStatus.inventory > 0 &&
          inventoryStatus.inventory < quantities[sku];

        if (store?.siteup && (!inventoryStatus.available || hasLimitedInventory)) {
          unavailableItems.push({
            key: item.dataset.key,
            title: item.querySelector(".cart-item__name")?.innerText,
            image: item.querySelector(".cart-item__image")?.src,
            quantity: requestedQuantity,
            inventory: inventoryStatus.inventory,
          });
        }
      });
    } catch (e) {
      console.error(e);
    }

    this.unavailableItems = unavailableItems;

    if (showWarning && store?.siteup && this.unavailableItems.length > 0) {
      this.showInventoryWarning();
    }
  }
  getInventoryCacheKey({ endpoint, skus, quantities, siteCode }) {
    return [
      endpoint,
      siteCode || "",
      [...skus].sort().join(","),
      JSON.stringify(
        Object.keys(quantities)
          .sort()
          .reduce((accumulator, sku) => {
            accumulator[sku] = quantities[sku];
            return accumulator;
          }, {}),
      ),
    ].join("::");
  }

  normalizeInventoryEntries(data) {
    const candidates =
      data?.items ||
      data?.data?.items ||
      data?.results ||
      data?.data?.results ||
      data?.inventories ||
      data?.data?.inventories ||
      data;

    if (Array.isArray(candidates)) return candidates;

    if (
      candidates &&
      typeof candidates === "object" &&
      !Array.isArray(candidates) &&
      !("sku" in candidates) &&
      !("is_available" in candidates) &&
      !("available" in candidates)
    ) {
      return Object.entries(candidates).map(([sku, value]) => ({
        sku,
        ...(typeof value === "object" && value !== null
          ? value
          : { inventory: value }),
      }));
    }

    return candidates ? [candidates] : [];
  }

  async fetchLocalInventoryStatuses({ skus, quantities, siteCode }) {
    const cacheKey = this.getInventoryCacheKey({
      endpoint: "local",
      skus,
      quantities,
      siteCode,
    });

    if (!this.inventoryCache[cacheKey]) {
      this.inventoryCache[cacheKey] = fetch(
        "https://shopify-big.bigcaring.com.my/api/outlet/check-inventory/local",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            skus,
            quantities,
            site_code: siteCode,
          }),
        },
      )
        .then((res) => res.json())
        .then((data) => {
          const inventoryMap = new Map();

          this.normalizeInventoryEntries(data).forEach((entry) => {
            const sku = String(entry?.sku || entry?.code || "").trim();
            if (!sku) return;
            inventoryMap.set(sku, this.getInventoryStatus(entry));
          });

          return inventoryMap;
        })
        .catch((error) => {
          delete this.inventoryCache[cacheKey];
          throw error;
        });
    }

    return this.inventoryCache[cacheKey];
  }
  getInventoryStatus(data) {
    const inventory = this.getInventoryQuantity(data);
    const hasExplicitAvailability =
      data?.is_available !== undefined || data?.available !== undefined;
    const inferredAvailability = inventory !== null ? inventory > 0 : false;

    return {
      available: hasExplicitAvailability
        ? Boolean(data?.is_available ?? data?.available)
        : inferredAvailability,
      inventory,
    };
  }
  getInventoryQuantity(data) {
    const quantity = Number(data?.inventory ?? data?.data?.inventory);

    return Number.isFinite(quantity) && quantity >= 0 ? quantity : null;
  }
  showInventoryWarning() {
    const modal = this.querySelector("#InventoryWarning");
    if (!modal) return;

    const list = modal.querySelector(".inventory-warning__list");
    const dedupedItems = Array.from(
      new Map(
        (this.unavailableItems || []).map((item) => [
          item.key || `${item.title}-${item.quantity}`,
          item,
        ]),
      ).values(),
    );

    list.innerHTML = dedupedItems
      .map(
        (item) => `
      <li style="display:flex;gap:16px;align-items:center;margin-bottom:10px;">

        <img
          src="${item.image}"
          style="width:50px;height:50px;object-fit:cover;border-radius:4px;"
        />

        <div style="flex:1;">
          <div style="font-size:14px; line-height: 1.2;">${item.title}</div>
          <div style="font-size:12px; color:red">${
            item.inventory > 0
              ? `Only ${item.inventory} available in this store`
              : "Not Available in this store"
          }</div>
        </div>
          <div style="font-size:12px;color:#666;">
            Qty: ${item.quantity}
          </div>
      </li>
    `,
      )
      .join("");

    modal.classList.remove("hidden");

    modal.querySelector("#CloseInventoryWarning").onclick = () => {
      modal.classList.add("hidden");
    };

    modal.querySelector("#RemoveUnavailableBtn").onclick =
      this.removeUnavailableItems.bind(this);
  }

  async removeUnavailableItems() {
    const updates = {};

    this.unavailableItems.forEach((item) => {
      if (item.key) {
        updates[item.key] = 0;
      }
    });

    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates }),
      });

      // ✅ 重新渲染 cart drawer（关键）
      fetch(`${window.routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
        .then((res) => res.json())
        .then((data) => {
          this.renderContents({
            sections: data,
          });
        });
    } catch (err) {
      console.error(err);
    }
  }
  renderInventory(item, inventoryStatus, requestedQuantity) {
    let el = item.querySelector(".cart-item__inventory");

    if (!el) {
      el = document.createElement("div");
      el.className = "cart-item__inventory";
      item.querySelector(".cart-item__details")?.appendChild(el);
    }

    const store = this.getSelectedStore();

    if (!store || !store.name) return;

    const hasLimitedInventory =
      inventoryStatus.inventory !== null &&
      inventoryStatus.inventory > 0 &&
      inventoryStatus.inventory < requestedQuantity;

    if (hasLimitedInventory) {
      el.innerHTML = `
        <span style="color:#c76900;font-size:12px;display:block;">
          Only ${inventoryStatus.inventory} available at ${store.name}
        </span>
      `;
      return;
    }

    if (!inventoryStatus.available) {
      el.innerHTML = `
        <span style="color:red;font-size:12px;">
          Pickup unavailable at ${store.name}
        </span>
      `;
    } else {
      el.innerHTML = `
        <span style="color:#000;font-size:12px;">
          Pickup available at ${store.name}
        </span>
      `;
    }
  }
  updateSelectedStore(storeData) {
    // 存储
    localStorage.setItem("selectedStore", JSON.stringify(storeData));
    window.dispatchEvent(
      new CustomEvent("selected-store:changed", {
        detail: { store: storeData, source: "cart" },
      }),
    );
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
